import {useEffect, useMemo, useState} from "react";
import {isArrayNullUndefinedOrEmpty, isNullOrUndefined} from "../../../services/Validator";
import SystemAppContentError from "../../errors/SystemAppContentError";
import SystemAppContentLoader from "../../loaders/SystemAppContentLoader";
import {Environment} from "../../../enums/Environment";
import {kFakeBrevetLevelAs} from "../../../services/Faker";
import {kBrevetLevelAsError, kEmptyResponseError} from "../../../constants/errors";
import {sendErrorToSentry} from "../../../services/Sentry";
import {get_brevet_levelAs} from "../../../api/levelA";
import BrevetLevelAsHeader from "../../headers/brevetScreen/BrevetLevelAsHeader";
import BrevetFilterDialog from "../../dialogs/brevetScreen/BrevetFilterDialog";
import BrevetLevelAHeaderTitleButton from "../../buttons/brevetScreen/BrevetLevelAHeaderTitleButton";
import BrevetLevelAsList from "../../lists/brevetScreen/BrevetLevelAsList";

export default function BrevetLevelAsContent() {
    // Used in BrevetScreen to display core content

    // PROPERTIES
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState(null)
    const [brevetLevelAs, setBrevetLevelAs] = useState([])
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
    const [sort, setSort] = useState({name: "Niveau", desc: true})
    const defaultFilters = {
        subthemes: [],
        grades: [],
        medias: [],
    }
    const [availableFilters, setAvailableFilters] = useState(null)
    const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
    const displayedLevelAs = useMemo(() => {
        let levelAs = [...(brevetLevelAs ?? [])];
        // ---- FILTERS
        // Subthemes (if not empty filters)
        if (appliedFilters.subthemes?.length) {
            const set = new Set(appliedFilters.subthemes);
            levelAs = levelAs.filter(levelA => set.has(levelA.levelASubtheme.name));
        }

        // Grades (if not empty filters)
        if (appliedFilters.grades?.length) {
            const set = new Set(appliedFilters.grades);
            levelAs = levelAs.filter(levelA => set.has(levelA.levelAGrade.name));
        }
        return levelAs;
    }, [brevetLevelAs, appliedFilters]);

    // HOOKS
    // ---- Triggered at rendering : set Grades
    useEffect(() => {
        getBrevetLevelAs()
    }, [])

    // BUILD
    if (!isNullOrUndefined(error))
        return <SystemAppContentError error={error}
                                      isLoading={isLoading}
                                      retryCallback={() => getBrevetLevelAs()} />
    if (isLoading)
        return <SystemAppContentLoader />
    return (
        <div className={"animate-appearSlow grow w-full flex flex-col justify-start min-h-0"}>
            {/** FILTER MODAL */}
            <BrevetFilterDialog open={isFilterModalOpen}
                                handleClose={processModalClosing}
                                availableFilters={availableFilters}
                                appliedFilters={appliedFilters}
                                applyDraftFilters={applyDraftFilters} />
            {/** CONTENT */}
            <BrevetLevelAsHeader onFilterButtonClick={processModalOpening}
                                 appliedFilters={appliedFilters}
                                 clearFilters={clearFilters} />
            <div className={"w-full grow flex flex-col justify-start items-center min-h-0"}>
                <div className={"mb-[2px] w-full h-[56px] min-h-[56px] bg-backgroundLight5 grid grid-cols-[5fr_3fr_120px_200px_120px] gap-[16px] items-center px-[24px] text-[12px] text-greyLight7 font-medium"}>
                    <BrevetLevelAHeaderTitleButton title={`Savoir-faire (${displayedLevelAs.length})`}
                                                   justifyStart={true}
                                                   isCurrentSort={sort.name === "Savoir-faire"}
                                                   isDesc={sort.desc}
                                                   onSortPressed={() => performSort("Savoir-faire")} />
                    <BrevetLevelAHeaderTitleButton title={"Chapitre"}
                                                   justifyStart={true}
                                                   isCurrentSort={sort.name === "Chapitre"}
                                                   isDesc={sort.desc}
                                                   onSortPressed={() => performSort("Chapitre")} />
                    <BrevetLevelAHeaderTitleButton title={"Niveau"}
                                                   justifyStart={true}
                                                   isCurrentSort={sort.name === "Niveau"}
                                                   isDesc={sort.desc}
                                                   onSortPressed={() => performSort("Niveau")} />
                    <BrevetLevelAHeaderTitleButton title={"Média"}
                                                   justifyStart={true} />
                    <BrevetLevelAHeaderTitleButton title={"Actions"}
                                                   justifyStart={false} />
                </div>
                <BrevetLevelAsList displayedLevelAs={displayedLevelAs}
                                   hasFilters={hasFilters()}
                                   clearFilters={clearFilters} />
            </div>
        </div>
    );

    // METHODS
    // ---- LOGIC : Apply filters
    function applyDraftFilters(draftFilters) {
        // Sécurise : on évite null/undefined + on garantit la forme
        const next = {
            subthemes: Array.isArray(draftFilters?.subthemes) ? draftFilters.subthemes : [],
            grades: Array.isArray(draftFilters?.grades) ? draftFilters.grades : [],
            medias: Array.isArray(draftFilters?.medias) ? draftFilters.medias : [],
        };
        setAppliedFilters(next)
        processModalClosing()
    }

    // ---- LOGIC : Check if table has current filters
    function hasFilters() {
        if (!appliedFilters) return 0;
        const subthemesCount = Array.isArray(appliedFilters.subthemes) ? appliedFilters.subthemes.length : 0;
        const gradesCount = Array.isArray(appliedFilters.grades) ? appliedFilters.grades.length : 0;
        const mediaCount = Array.isArray(appliedFilters.media) ? appliedFilters.media.length : 0;
        return subthemesCount + gradesCount + mediaCount > 0
    }

    // ---- UX/UI : Empty appliedFilters
    function clearFilters() {
        setAppliedFilters(defaultFilters)
    }

    // ---- UX/UI : Perform sort logic
    function performSort(pressedSortName) {
        if (sort.name === pressedSortName) {
            switch (pressedSortName) {
                case "Niveau":
                    setBrevetLevelAs(sortByGrade(!sort.desc));
                    break;
                case "Chapitre":
                    setBrevetLevelAs(sortBySubtheme(!sort.desc));
                    break;
                case "Savoir-faire":
                    setBrevetLevelAs(sortByTask(!sort.desc));
                    break;
                default:
                    break;
            }
            setSort(prev => ({
                ...prev,
                desc: !prev.desc
            }));
        } else {
            switch (pressedSortName) {
                case "Niveau":
                    setBrevetLevelAs(sortByGrade(true));
                    setSort({
                        name: pressedSortName,
                        desc: true
                    });
                    break;
                case "Chapitre":
                    setBrevetLevelAs(sortBySubtheme(false));
                    setSort({
                        name: pressedSortName,
                        desc: false
                    });
                    break;
                case "Savoir-faire":
                    setBrevetLevelAs(sortByTask(false));
                    setSort({
                        name: pressedSortName,
                        desc: false
                    });
                    break;
                default:
                    break;
            }
        }
    }

    // ---- UX/UI : sort by LevelA task
    function sortByTask(isDsc) {
        return [...brevetLevelAs].sort((a, b) => {

            // 1️⃣ Savoir-faire (variable)
            const taskCompare = isDsc
                ? b.task.localeCompare(a.task, "fr")
                : a.task.localeCompare(b.task, "fr");

            if (taskCompare !== 0) {
                return taskCompare;
            }

            // 2️⃣ Chapitre (ASC)
            const subthemeCompare = a.levelASubtheme.name.localeCompare(
                b.levelASubtheme.name,
                "fr"
            );
            if (subthemeCompare !== 0) {
                return subthemeCompare;
            }

            // 3️⃣ Niveau (DESC logique scolaire)
            return b.levelAGrade.rank - a.levelAGrade.rank;
        });
    }

    // ---- UX/UI : sort by LevelA Subtheme name
    function sortBySubtheme(isDsc) {
        return [...brevetLevelAs].sort((a, b) => {

            // 1️⃣ Chapitre (variable)
            const subthemeCompare = isDsc
                ? b.levelASubtheme.name.localeCompare(a.levelASubtheme.name, "fr")
                : a.levelASubtheme.name.localeCompare(b.levelASubtheme.name, "fr");

            if (subthemeCompare !== 0) {
                return subthemeCompare;
            }

            // 2️⃣ Savoir-faire (ASC)
            const taskCompare = a.task.localeCompare(b.task, "fr");
            if (taskCompare !== 0) {
                return taskCompare;
            }

            // 3️⃣ Niveau (DESC logique scolaire)
            return b.levelAGrade.rank - a.levelAGrade.rank;
        });
    }

    // ---- UX/UI : sort by LevelA Grade rank
    function sortByGrade(isDsc) {
        return [...brevetLevelAs].sort((a, b) => {
            // Rank (variable)
            if (a.levelAGrade.rank !== b.levelAGrade.rank) {
                return isDsc
                    ? b.levelAGrade.rank - a.levelAGrade.rank
                    : a.levelAGrade.rank - b.levelAGrade.rank
            }

            // Subtheme (ASC)
            const subthemeCompare = a.levelASubtheme.name.localeCompare(
                b.levelASubtheme.name,
                "fr"
            );
            if (subthemeCompare !== 0) {
                return subthemeCompare;
            }

            // Task (ASK)
            return a.task.localeCompare(b.task, "fr");
        });
    }

    // ---- API : Fetch flash levels from the api
    function getBrevetLevelAs() {
        // <<<< For .env.faker ----
        if (process.env.REACT_APP_ENV === Environment.FAKER) {
            setBrevetLevelAs(kFakeBrevetLevelAs)
            setIsLoading(false)
            setError(null)
            return
        }
        // ---- For .env.faker >>>>

        // Switch loader on and clear error
        setIsLoading(true)
        setError(null)
        // Perform
        get_brevet_levelAs()
            .then((res) => {
                if (isNullOrUndefined(res.items) || isArrayNullUndefinedOrEmpty(res.items) || isNullOrUndefined(res.filters) || isArrayNullUndefinedOrEmpty(res.filters)) {
                    setError(kEmptyResponseError)
                    sendErrorToSentry(kEmptyResponseError.sentry, "fetching brevet LevelAs from BrevetCoreContent")
                } else {
                    setBrevetLevelAs(res.items)
                    setAvailableFilters(res.filters)
                }
            }).catch((err) => {
            console.error(err)
            setError(kBrevetLevelAsError)
            sendErrorToSentry(kBrevetLevelAsError.sentry, "BrevetCoreContent")
        }).finally(() => {
            setIsLoading(false)
        })
    }

    // ---- UX/UI : Open modal process
    function processModalOpening() {
        if (!isFilterModalOpen) {
            setIsFilterModalOpen(true);
        }
    }

    // ---- UX/UI : Close modal process
    function processModalClosing() {
        if (isFilterModalOpen) {
            setIsFilterModalOpen(false)
        }
    }
}